import { useRef, useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link } from "react-router-dom";
import axios from "axios";

const NewArrivals = () => {
  const scrollRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [newArrivals, setNewArrivals] = useState([]);

  useEffect(() => {
    const fetchNewArrivals = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/products/new-arrivals`
        );
        setNewArrivals(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchNewArrivals();
  }, []);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setInitialScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = initialScrollLeft - (x - startX) * 2;
  };

  const handleMouseUpOrLeave = () => setIsDragging(false);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  const updateScrollButtons = () => {
    const c = scrollRef.current;
    if (!c) return;
    setCanScrollLeft(c.scrollLeft > 0);
    setCanScrollRight(c.scrollLeft < c.scrollWidth - c.clientWidth);
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", updateScrollButtons);
    updateScrollButtons();
    return () => container.removeEventListener("scroll", updateScrollButtons);
  }, [newArrivals]);

  return (
    <section className="na-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Exo+2:ital,wght@0,200;0,300;0,400;0,500;0,600;1,200;1,300&display=swap');

        .na-root {
          background: #000;
          padding: 80px 0 72px;
          overflow: hidden;
          font-family: 'Exo 2', sans-serif;
        }

        /* ── HEADER ── */
        .na-header {
          max-width: 1400px;
          margin: 0 auto 40px;
          padding: 0 48px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
        }

        .na-eyebrow {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.5em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.3);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .na-eyebrow::before {
          content: '';
          width: 24px;
          height: 1px;
          background: rgba(255,255,255,0.2);
          display: block;
          flex-shrink: 0;
        }

        .na-title {
          font-size: clamp(36px, 4.5vw, 60px);
          font-weight: 200;
          color: #fff;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
          margin: 0;
        }
        .na-title span {
          font-weight: 600;
        }

        .na-header-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 18px;
        }

        .na-subtitle {
          font-size: 10px;
          font-weight: 300;
          letter-spacing: 0.22em;
          color: rgba(255,255,255,0.28);
          text-align: right;
          line-height: 2.2;
          text-transform: uppercase;
        }

        .na-arrows {
          display: flex;
          gap: 4px;
        }

        .na-arrow {
          width: 40px;
          height: 40px;
          border: 1px solid rgba(255,255,255,0.18);
          background: transparent;
          color: rgba(255,255,255,0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .na-arrow:hover:not(:disabled) {
          background: #fff;
          color: #000;
          border-color: #fff;
        }
        .na-arrow:disabled {
          opacity: 0.15;
          cursor: not-allowed;
        }

        /* ── RULE ── */
        .na-rule {
          max-width: 1400px;
          margin: 0 auto 36px;
          padding: 0 48px;
        }
        .na-rule hr {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        /* ── SCROLL STRIP ── */
        .na-scroll {
          display: flex;
          gap: 2px;
          overflow-x: scroll;
          padding: 0 48px;
          max-width: 1400px;
          margin: 0 auto;
          box-sizing: border-box;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .na-scroll::-webkit-scrollbar { display: none; }

        /* ── CARD ── */
        .na-card {
          position: relative;
          flex-shrink: 0;
          width: clamp(220px, 23vw, 310px);
          height: 440px;
          overflow: hidden;
          background: #0a0a0a;
        }

        .na-card-link {
          display: block;
          width: 100%;
          height: 100%;
          text-decoration: none;
          position: relative;
          overflow: hidden;
        }

        /* Image fills card minus caption height */
        .na-card-img-wrap {
          width: 100%;
          height: calc(100% - 56px);
          overflow: hidden;
        }

        .na-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .na-card:hover .na-card-img {
          transform: scale(1.05);
        }

        /* NEW badge */
        .na-badge {
          position: absolute;
          top: 14px;
          left: 14px;
          z-index: 3;
          background: #fff;
          color: #000;
          font-family: 'Exo 2', sans-serif;
          font-size: 7px;
          font-weight: 600;
          letter-spacing: 0.38em;
          text-transform: uppercase;
          padding: 4px 9px;
        }

        /* ── CAPTION — solid black strip, always readable ── */
        .na-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 56px;
          background: #000;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding: 0 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .na-product-name {
          font-family: 'Exo 2', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .na-product-price {
          font-family: 'Exo 2', sans-serif;
          font-size: 13px;
          font-weight: 300;
          letter-spacing: 0.1em;
          color: rgba(255,255,255,0.6);
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── FOOTER ── */
        .na-footer {
          max-width: 1400px;
          margin: 44px auto 0;
          padding: 0 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .na-cta {
          font-family: 'Exo 2', sans-serif;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: #fff;
          text-decoration: none;
          position: relative;
          padding-bottom: 6px;
        }
        .na-cta::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%;
          height: 1px;
          background: rgba(255,255,255,0.25);
          transition: background 0.25s ease;
        }
        .na-cta:hover::after {
          background: #fff;
        }

        .na-piece-count {
          font-family: 'Exo 2', sans-serif;
          font-size: 9px;
          font-weight: 200;
          letter-spacing: 0.3em;
          color: rgba(255,255,255,0.18);
          text-transform: uppercase;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .na-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 20px;
            padding: 0 20px;
          }
          .na-header-right { align-items: flex-start; }
          .na-subtitle { text-align: left; }
          .na-scroll { padding: 0 20px; }
          .na-rule { padding: 0 20px; }
          .na-footer { padding: 0 20px; }
          .na-card { height: 380px; }
        }
      `}</style>

      {/* HEADER */}
      <div className="na-header">
        <div>
          <div className="na-eyebrow">Season Drop</div>
          <h2 className="na-title">New <span>Arrivals</span></h2>
        </div>
        <div className="na-header-right">
          <p className="na-subtitle">
            Bold designs · Timeless essentials<br />
            Wear what you mean
          </p>
          <div className="na-arrows">
            <button
              className="na-arrow"
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <FiChevronLeft size={16} />
            </button>
            <button
              className="na-arrow"
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* DIVIDER */}
      <div className="na-rule"><hr /></div>

      {/* SCROLL STRIP */}
      <div
        ref={scrollRef}
        className="na-scroll"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
      >
        {newArrivals.map((product) => (
          <div key={product._id} className="na-card">
            <span className="na-badge">New</span>
            <Link to={`/product/${product._id}`} className="na-card-link">
              <div className="na-card-img-wrap">
                <img
                  src={product.images?.[0]?.url}
                  alt={product.name || "Product"}
                  className="na-card-img"
                  draggable="false"
                />
              </div>
              <div className="na-caption">
                <span className="na-product-name">{product.name}</span>
                <span className="na-product-price">₹{product.price}</span>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* FOOTER */}
      <div className="na-footer">
        <Link to="/collections/all" className="na-cta">View All Pieces</Link>
        {newArrivals.length > 0 && (
          <span className="na-piece-count">{newArrivals.length} pieces</span>
        )}
      </div>
    </section>
  );
};

export default NewArrivals;
